import { GameState } from '../game/GameState';
import { DataRegistry } from './DataRegistry';
import { EconomyManager } from './EconomyManager';
import { ModifierManager } from './ModifierManager';

export class RecruitmentManager {
  static readonly instance = new RecruitmentManager();

  recruit(factionId: string, provinceId: string, unitId: string) {
    const campaign = GameState.instance.requireCampaign();
    const registry = DataRegistry.instance;
    const factionData = registry.factions[factionId];
    const unit = registry.units[unitId];
    const province = campaign.provinces[provinceId];
    if (!factionData || !unit || !province) return false;
    if (province.owner !== factionId || province.controller !== factionId) {
      GameState.instance.log('Recruitment requires a securely controlled province.', 'warning');
      return false;
    }
    if (!factionData.roster.includes(unitId)) {
      GameState.instance.log(`${unit.name} is not in this faction roster.`, 'warning');
      return false;
    }
    const faction = campaign.factions[factionId];
    const cost = ModifierManager.instance.effectiveRecruitmentCost(factionId, unit);
    if (faction.treasury < cost) {
      GameState.instance.log('Not enough gold to recruit.', 'warning');
      return false;
    }
    if (faction.manpower < unit.manpower) {
      GameState.instance.log(`${registry.factions[factionId]?.name ?? factionId} lacks the ${unit.manpower} available manpower required.`, 'warning');
      return false;
    }

    const levy = Math.max(0.5, Math.ceil(unit.manpower / 250) * 0.5);
    const available = (province.populationGroups.peasants ?? 0) + (province.populationGroups.artisans ?? 0) * 0.5;
    if (available < levy || province.population - levy < 5) {
      GameState.instance.log(`${registry.provinces[provinceId]?.name} lacks enough available population for this unit.`, 'warning');
      return false;
    }

    faction.treasury -= cost;
    faction.manpower -= unit.manpower;
    const peasantLevy = Math.min(province.populationGroups.peasants, levy);
    province.populationGroups.peasants = Math.max(0, province.populationGroups.peasants - peasantLevy);
    const artisanLevy = Math.max(0, levy - peasantLevy);
    province.populationGroups.artisans = Math.max(0, province.populationGroups.artisans - artisanLevy);
    province.population = Math.max(5, province.population - levy);
    province.populationGroups.soldiers += levy;
    province.workforce = EconomyManager.instance.workforceCapacity(province);
    province.prosperity = Math.max(0, province.prosperity - levy * 0.15);

    campaign.recruitmentQueue.push({
      id: `rq_${campaign.turn}_${campaign.recruitmentQueue.length}_${unitId}`,
      factionId,
      provinceId,
      unitId,
      remainingMonths: ModifierManager.instance.effectiveRecruitmentMonths(factionId, unit),
    });
    GameState.instance.log(`Recruiting ${unit.name} in ${registry.provinces[provinceId]?.name}; ${unit.manpower} manpower and ${levy.toFixed(1)} population committed.`, 'success');
    return true;
  }

  processMonth() {
    const campaign = GameState.instance.requireCampaign();
    const completed = [];
    for (const item of campaign.recruitmentQueue) {
      item.remainingMonths--;
      if (item.remainingMonths <= 0) completed.push(item);
    }
    campaign.recruitmentQueue = campaign.recruitmentQueue.filter((item) => item.remainingMonths > 0);
    for (const item of completed) {
      const registry = DataRegistry.instance;
      const unit = registry.units[item.unitId];
      if (!unit) continue;
      const armyId = `${item.factionId}_${item.provinceId}_recruits`;
      if (!campaign.armies[armyId]) {
        const commanderId = `commander_${armyId}`;
        campaign.armies[armyId] = {
          id: armyId,
          factionId: item.factionId,
          provinceId: item.provinceId,
          stance: 'hold',
          commanderId,
          templateId: 'balanced_host',
          units: [],
        };
        campaign.commanders[commanderId] = {
          id: commanderId,
          name: `Captain of ${registry.provinces[item.provinceId]?.name ?? item.provinceId}`,
          factionId: item.factionId,
          armyId,
          martial: 2,
          logistics: 2,
          siege: 1,
          loyalty: 65,
          traits: ['newly_promoted'],
          victories: 0,
          defeats: 0,
        };
      }
      campaign.armies[armyId].units.push({
        id: `${armyId}_${item.unitId}_${campaign.turn}_${campaign.armies[armyId].units.length}`,
        unitId: item.unitId,
        strength: unit.manpower,
        morale: unit.morale + ModifierManager.instance.forFaction(item.factionId).battleMoraleBonus,
        experience: ModifierManager.instance.hasGunpowderEdge(item.factionId, unit) ? 8 : 0,
      });
      GameState.instance.log(`${unit.name} raised in ${registry.provinces[item.provinceId]?.name}.`, 'success');
    }
  }
}
